import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {NzAlertModule} from 'ng-zorro-antd/alert';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NzRadioModule} from 'ng-zorro-antd/radio';
import {NzSelectModule} from 'ng-zorro-antd/select';
import {AccountApiService} from '../../services/apis/account.api.service';
import {SupabaseAuthService} from '../../services/supabase-auth.service';

export interface SecurityVerificationDialogData {
  action: string;
  resource: Record<string, unknown>;
}

export interface SecurityVerificationResult {
  ticket: string;
  expires_at: string;
  method: 'email_otp' | 'mfa';
}

@Component({
  selector: 'app-security-verification-dialog',
  imports: [
    FormsModule,
    TranslocoPipe,
    NzAlertModule,
    NzButtonModule,
    NzInputModule,
    NzRadioModule,
    NzSelectModule,
  ],
  templateUrl: './security-verification-dialog.component.html',
  styleUrl: './security-verification-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecurityVerificationDialogComponent implements OnInit {
  readonly method = signal<'email_otp' | 'mfa'>('email_otp');
  readonly email = signal('');
  readonly emailCode = signal('');
  readonly emailCooldown = signal(0);
  readonly emailSending = signal(false);
  readonly factors = signal<{id: string; friendly_name?: string}[]>([]);
  readonly selectedFactorId = signal('');
  readonly mfaCode = signal('');
  readonly loading = signal(false);
  readonly error = signal('');

  private readonly data = inject<SecurityVerificationDialogData>(NZ_MODAL_DATA);
  readonly modalRef = inject(NzModalRef);
  private readonly accountApi = inject(AccountApiService);
  private readonly auth = inject(SupabaseAuthService);
  private readonly destroyRef = inject(DestroyRef);
  private emailCooldownTimer: ReturnType<typeof setInterval> | null = null;

  async ngOnInit() {
    this.destroyRef.onDestroy(() => this.clearEmailCooldown());
    const [session, factors] = await Promise.all([
      this.auth.getSession(),
      this.auth.listTotpFactors().catch(() => []),
    ]);
    this.email.set(session?.user.email ?? '');
    this.factors.set(
      factors.map((factor) => ({id: factor.id, friendly_name: factor.friendly_name})),
    );
    this.selectedFactorId.set(factors[0]?.id ?? '');
  }

  async sendEmailCode() {
    if (this.emailSending() || this.loading() || this.emailCooldown() > 0) {
      return;
    }

    this.emailSending.set(true);
    this.error.set('');
    try {
      const response = await this.accountApi.getCaptcha({
        username: this.email(),
        action: this.data.action,
      });
      if (response.code) throw new Error(response.message);
      this.startEmailCooldown(60);
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Verification code failed.');
    } finally {
      this.emailSending.set(false);
    }
  }

  async verify() {
    this.loading.set(true);
    this.error.set('');
    try {
      const method = this.method();
      if (method === 'mfa') {
        await this.auth.challengeAndVerifyTotp(this.selectedFactorId(), this.mfaCode());
      }
      const response = await this.accountApi.issueSecurityVerification({
        action: this.data.action,
        resource: this.data.resource,
        method,
        ...(method === 'email_otp' ? {code: this.emailCode()} : {}),
      });
      if (response.code || !response.result?.ticket) throw new Error(response.message);
      this.modalRef.close({...response.result, method} satisfies SecurityVerificationResult);
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Security verification failed.');
    } finally {
      this.loading.set(false);
    }
  }

  private startEmailCooldown(seconds: number): void {
    this.clearEmailCooldown();
    this.emailCooldown.set(seconds);
    this.emailCooldownTimer = setInterval(() => {
      const next = this.emailCooldown() - 1;
      if (next <= 0) {
        this.clearEmailCooldown();
        return;
      }
      this.emailCooldown.set(next);
    }, 1000);
  }

  private clearEmailCooldown(): void {
    if (this.emailCooldownTimer) {
      clearInterval(this.emailCooldownTimer);
      this.emailCooldownTimer = null;
    }
    this.emailCooldown.set(0);
  }
}
